// src/services/userMasterService.jsx
import axios from './axios.jsx';

const API_BASE_URL = '/api/v1/signups';

// Predefined college list for filtering and statistics
export const COLLEGES = [
  { id: 'pict', name: 'PICT College', filter: 'PICT' },
  { id: 'asian', name: 'Asian College', filter: 'Asian' },
  { id: 'trinity', name: 'Trinity/KJ College', filter: 'Trinity' },
  { id: 'dbatu', name: 'DBATU University', filter: 'DBATU' },
  { id: 'sinhgad', name: 'Sinhgad College Of Science', filter: 'Sinhgad' },
  { id: 'sndt', name: 'SNDT College', filter: 'SNDT' },
  { id: 'other', name: 'Another Colleges / Outside India', filter: '' }
];

// Predefined domains list
export const DOMAINS = [
  { id: 'fullstack', name: 'Full Stack Developer' },
  { id: 'aiml', name: 'AI/ML' },
  { id: 'cloud', name: 'Cloud Computing' },
  { id: 'graphics', name: 'Graphics Designer' },
  { id: 'cybersecurity', name: 'Cybersecurity' },
  { id: 'devops', name: 'DevOps' }
];

// Cache for API responses
let usersCache = null;
let lastFetchTime = 0;
const CACHE_DURATION = 30000; // 30 seconds cache

const userMasterService = {
  getAllUsers: async (forceRefresh = false) => {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (!forceRefresh && usersCache && (now - lastFetchTime) < CACHE_DURATION) {
      console.log('Returning cached users data');
      return usersCache;
    }
    
    try {
      console.log('Fetching users from API...');
      const response = await axios.get(`${API_BASE_URL}/`);
      
      let users = [];
      if (Array.isArray(response.data)) {
        users = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        users = response.data.data;
      } else if (response.data && Array.isArray(response.data.users)) {
        users = response.data.users;
      } else {
        users = [];
      }
      
      // Update cache
      usersCache = users;
      lastFetchTime = now;
      
      return users;
    } catch (error) {
      console.error('Error fetching users:', error);
      // Return cached data if available, even if expired
      if (usersCache) {
        console.log('API failed, returning cached data');
        return usersCache;
      }
      return [];
    }
  },

  getUserById: async (signUpId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/${signUpId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      throw error;
    }
  },

  getUserByEmail: async (email) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/profile/${email}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user by email:', error);
      throw error;
    }
  },

  searchUsers: async (username) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/search`, {
        params: { username }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  },

  createUser: async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/`, userData);
      // Invalidate cache on create
      usersCache = null;
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  updateUser: async (signUpId, userData) => {
    try {
      const { signUpId: _, _id, id, ...cleanUserData } = userData;
      const response = await axios.put(`${API_BASE_URL}/${signUpId}`, cleanUserData);
      // Invalidate cache on update
      usersCache = null;
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/update-profile`, profileData);
      // Invalidate cache on profile update
      usersCache = null;
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  },

  deleteUser: async (signUpId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/${signUpId}`);
      // Invalidate cache on delete
      usersCache = null;
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  getUsersByCollege: async (collegeName) => {
    try {
      const allUsers = await userMasterService.getAllUsers();
      return allUsers.filter(user => 
        user.collegeName && user.collegeName.toLowerCase().includes(collegeName.toLowerCase())
      );
    } catch (error) {
      return [];
    }
  },

  getUsersByDomain: async (domain) => {
    try {
      const allUsers = await userMasterService.getAllUsers();
      return allUsers.filter(user => 
        user.areaOfInterest && user.areaOfInterest.toLowerCase().includes(domain.toLowerCase())
      );
    } catch (error) {
      return [];
    }
  },

  deleteMultipleUsers: async (signUpIds) => {
    try {
      const results = await Promise.allSettled(
        signUpIds.map(id => userMasterService.deleteUser(id))
      );
      // Invalidate cache on bulk delete
      usersCache = null;
      return {
        success: results.filter(r => r.status === 'fulfilled').length,
        failed: results.filter(r => r.status === 'rejected').length,
        total: signUpIds.length
      };
    } catch (error) {
      return { success: 0, failed: signUpIds.length, total: signUpIds.length };
    }
  },
  
  // Method to clear cache manually if needed
  clearCache: () => {
    usersCache = null;
    lastFetchTime = 0;
  }
};

export default userMasterService;